import {inject, Injectable, signal} from '@angular/core';
import Keycloak from 'keycloak-js';
import {catchError, map, Observable, shareReplay, tap, throwError, timeout, retry, timer} from 'rxjs';
import {apiVersion, traceLabBaseUrl} from '@app/core/utils/service.helper';
import {UserRole} from '../types/user.types';
import {Operateur} from '@core/models/operateur.model';
import {BaseService} from '@core/services/base.service';
import {ODataResponse} from '@core/models/tools/odata-response.model';
import {environment} from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService {
  private readonly keycloak = inject(Keycloak);

  private readonly _operator = signal<Operateur | null>(null);
  private readonly _isAdmin = signal<boolean>(false);
  private readonly _userRole = signal<UserRole>(UserRole.USER);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _operatorLoaded = signal<boolean>(false);
  private readonly _initializationError = signal<string | null>(null);

  readonly operator = this._operator.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly operatorLoaded = this._operatorLoaded.asReadonly();
  readonly initializationError = this._initializationError.asReadonly();
  readonly userRole = this._userRole.asReadonly();

  private operatorRequest$: Observable<Operateur> | null = null;

  initialize(): Observable<Operateur> {
    // Return cached operator if already loaded
    if (this._operatorLoaded() && this._operator()) {
      return new Observable(subscriber => {
        subscriber.next(this._operator()!);
        subscriber.complete();
      });
    }

    // Return ongoing request if already loading
    if (this.operatorRequest$) {
      return this.operatorRequest$;
    }

    const email = this.getConnectedUserEmail();
    if (!email) {
      const error = new Error('Email not found in Keycloak token');
      this._initializationError.set(error.message);
      return throwError(() => error);
    }

    this._isLoading.set(true);
    this._initializationError.set(null);

    this.operatorRequest$ = this.getOperatorByEmail(email).pipe(
      // Add timeout to prevent infinite loading
      timeout(15000), // 15 second timeout (increased from 10s)
      // Retry on failure with exponential backoff (for slow/unstable WiFi)
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.log(`⚠️ Retry attempt ${retryCount} after error:`, error.message);
          // Exponential backoff: 1s, 2s, 4s
          return timer(Math.pow(2, retryCount - 1) * 1000);
        },
      }),
      tap(operator => {
        this._operator.set(operator);
        this._operatorLoaded.set(true);
        this._isLoading.set(false);
        this.updateUserData();
        this.operatorRequest$ = null;
      }),
      catchError(error => {
        this._isLoading.set(false);
        const errorMessage = error.name === 'TimeoutError'
          ? 'Request timeout - please check your connection and refresh the page'
          : error.message || 'Failed to load user data';
        this._initializationError.set(errorMessage);
        this.operatorRequest$ = null;
        console.error('❌ Error initializing user data after retries:', error);
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.operatorRequest$;
  }

  getKeycloakRoles(): string[] {
    const clientId = environment.keycloak.initConfig.clientId;
    return this.keycloak.resourceAccess?.[clientId]?.roles || [];
  }

  hasAnyRole(requiredRoles: string[]): boolean {
    if (!requiredRoles.length) {
      return true;
    }

    const userRoles = this.getKeycloakRoles();
    return requiredRoles.some(role => userRoles.includes(role));
  }

  isAdmin(): boolean {
    return this._userRole() === UserRole.ADMIN;
  }

  getAccessibleRoutes(): string[] {
    const role = this._userRole();

    switch (role) {
      case UserRole.ADMIN:
        return ['/training-codes', '/status-management', '/batch-management', '/digital-batch'];
      case UserRole.CLIENT_SERVICE:
        return ['/training-codes', '/status-management', '/digital-batch'];
      case UserRole.PRODUCTION:
        return ['/training-codes', '/status-management'];
      case UserRole.QUALITY:
        return ['/training-codes', '/batch-management'];
      case UserRole.LOGISTIC:
        return ['/training-codes', '/batch-management'];
      case UserRole.METHOD:
      case UserRole.USER:
      default:
        return ['/training-codes'];
    }
  }

  getDefaultRoute(): string {
    const role = this._userRole();
    return role === UserRole.LOGISTIC ? '/batch-management' : '/training-codes';
  }

  canAccessRoute(route: string): boolean {
    const accessibleRoutes = this.getAccessibleRoutes();
    return accessibleRoutes.includes(route);
  }

  reset(): void {
    this._operator.set(null);
    this._isAdmin.set(false);
    this._userRole.set(UserRole.USER);
    this._isLoading.set(false);
    this._operatorLoaded.set(false);
    this._initializationError.set(null);
    this.operatorRequest$ = null;
  }

  private getConnectedUserEmail(): string | null {
    return this.keycloak.tokenParsed?.['email'] || null;
  }

  private getOperatorByEmail(email: string): Observable<Operateur> {
    // Using OData filter to find operator by email
    const url = `${traceLabBaseUrl}/Operateurs?${apiVersion}&$filter=AdresseEmail eq '${email}'&$select=IdOperateur,Nom,Prenom,AdresseEmail,IdTypeOperateur,Login`;

    return this.http.get<ODataResponse<Operateur>>(url).pipe(
      map(response => {
        if (!response.value || response.value.length === 0) {
          throw new Error(`No operator found for email: ${email}`);
        }
        return response.value[0];
      })
    );
  }

  private updateUserData(): void {
    // Update user role based on Keycloak roles
    const keycloakRoles = this.getKeycloakRoles();
    this.setUserRoleFromKeycloakRoles(keycloakRoles);
  }

  private setUserRoleFromKeycloakRoles(keycloakRoles: string[]): void {
    // Priority: ADMIN > METHOD > CLIENT_SERVICE > PRODUCTION > LOGISTIC > QUALITY > USER
    if (keycloakRoles.includes(UserRole.ADMIN)) {
      this._userRole.set(UserRole.ADMIN);
    } else if (keycloakRoles.includes(UserRole.METHOD)) {
      this._userRole.set(UserRole.METHOD);
    }  else if (keycloakRoles.includes(UserRole.CLIENT_SERVICE)) {
      this._userRole.set(UserRole.CLIENT_SERVICE);
    } else if (keycloakRoles.includes(UserRole.PRODUCTION)) {
      this._userRole.set(UserRole.PRODUCTION);
    } else if (keycloakRoles.includes(UserRole.LOGISTIC)) {
      this._userRole.set(UserRole.LOGISTIC);
    } else if (keycloakRoles.includes(UserRole.QUALITY)) {
      this._userRole.set(UserRole.QUALITY);
    } else {
      this._userRole.set(UserRole.USER);
    }
  }
}
